/* Kultured Variant Picker — Paper design behaviour.
   Progressive enhancement only. The native <select> and radios remain the
   source of truth for variant logic; this file never changes variant state
   except by driving those existing controls. */
(function () {
  'use strict';

  var SIZE_LABELS = {
    'x-small': 'XS',
    'xsmall': 'XS',
    'small': 'S',
    'medium': 'M',
    'large': 'L',
    'x-large': 'XL',
    'xlarge': 'XL',
    'xx-large': 'XXL',
    'xxlarge': 'XXL'
  };

  function sizeParts(text) {
    // "8x12\" (X-Small)" / "12x18″ (Small)" / "(A2) 16x24″ (Medium)"
    var letter = '';
    var m = text.match(/\(([^)]*)\)\s*$/);
    if (m) {
      var key = m[1].toLowerCase().replace(/\s+/g, '-');
      letter = SIZE_LABELS[key] || m[1];
    }
    var dims = text.replace(/\([^)]*\)/g, '').trim();
    dims = dims.replace(/\s*[x×]\s*/i, ' × ').replace(/["″]/g, '"');
    if (dims && !/"$/.test(dims)) dims += '"';
    return { letter: letter || dims, dims: letter ? dims : '' };
  }

  function buildSizeGrid(wrapper) {
    var select = wrapper.querySelector('select.select__select');
    if (!select || wrapper.querySelector('.kvp-size-grid')) return;

    var badgeValue = (wrapper.getAttribute('data-kvp-badge') || '').trim();
    var badgeLetter = (wrapper.getAttribute('data-kvp-badge-letter') || '').trim().toUpperCase();
    var badgeText = (wrapper.getAttribute('data-kvp-badge-text') || '').trim();

    var grid = document.createElement('div');
    grid.className = 'kvp-size-grid';
    grid.setAttribute('role', 'radiogroup');
    grid.setAttribute('aria-label', 'Size');

    Array.prototype.forEach.call(select.options, function (opt) {
      var parts = sizeParts(opt.textContent.trim());
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'kvp-size';
      btn.setAttribute('role', 'radio');
      btn.dataset.value = opt.value;
      btn.setAttribute('aria-checked', opt.selected ? 'true' : 'false');
      if (opt.disabled) btn.disabled = true;

      var letter = document.createElement('span');
      letter.className = 'kvp-size__letter';
      letter.textContent = parts.letter;
      btn.appendChild(letter);

      if (parts.dims) {
        var dims = document.createElement('span');
        dims.className = 'kvp-size__dims';
        dims.textContent = parts.dims;
        btn.appendChild(dims);
      }

      var isBadged = badgeValue
        ? opt.value === badgeValue
        : badgeLetter && parts.letter.toUpperCase() === badgeLetter;
      if (badgeText && isBadged && !opt.disabled) {
        btn.classList.add('kvp-size--badged');
        var badge = document.createElement('span');
        badge.className = 'kvp-size__badge';
        badge.textContent = badgeText;
        btn.appendChild(badge);
      }

      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        select.value = btn.dataset.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });

      grid.appendChild(btn);
    });

    select.parentNode.parentNode.appendChild(grid);
  }

  /* Heading reads "Size · L · 20 × 28"" per the design. */
  function syncSizeHeading(wrapper) {
    var select = wrapper.querySelector('select.select__select');
    var out = wrapper.querySelector('.sel_siz');
    if (!select || !out) return;
    var opt = select.options[select.selectedIndex];
    if (!opt) return;
    var p = sizeParts(opt.textContent.trim());
    // Paper writes the heading as "L · 20 × 28 in" while the cards use inch marks.
    var dims = p.dims.replace(/"$/, ' in');
    out.textContent = dims ? p.letter + ' · ' + dims : p.letter;
  }

  /* Remove any duplicate size card that is only there as an unavailable
     data artefact (the "(A2) 16x24″ (Medium)" twin). Runs on every sync so it
     self-heals regardless of when the grid was built. */
  function pruneDuplicates(grid) {
    var groups = {};
    Array.prototype.slice.call(grid.querySelectorAll('.kvp-size')).forEach(function (btn) {
      var letter = (btn.querySelector('.kvp-size__letter') || {}).textContent || '';
      var dims = (btn.querySelector('.kvp-size__dims') || {}).textContent || '';
      var key = letter + '|' + dims;
      (groups[key] = groups[key] || []).push(btn);
    });

    Object.keys(groups).forEach(function (key) {
      var group = groups[key];
      if (group.length < 2) return;
      // Keep the buyable one; a size should never appear twice.
      var keep = group.filter(function (b) {
        return !b.disabled;
      })[0] || group[0];
      group.forEach(function (b) {
        if (b !== keep && b.parentNode) b.parentNode.removeChild(b);
      });
    });
  }

  function syncSizeGrid(wrapper) {
    var select = wrapper.querySelector('select.select__select');
    var grid = wrapper.querySelector('.kvp-size-grid');
    syncSizeHeading(wrapper);
    if (!select || !grid) return;
    pruneDuplicates(grid);
    Array.prototype.forEach.call(grid.querySelectorAll('.kvp-size'), function (btn) {
      var opt = Array.prototype.filter.call(select.options, function (o) {
        return o.value === btn.dataset.value;
      })[0];
      btn.setAttribute('aria-checked', opt && opt.selected ? 'true' : 'false');
      btn.disabled = !!(opt && opt.disabled);
    });
  }

  /* Finish: split "Print (Unframed)" into title + subtitle.
     Claim subtitles come from theme settings via data attributes. */
  function enhanceFinish(fieldset) {
    var claims = {};
    try {
      claims = JSON.parse(fieldset.getAttribute('data-kvp-claims') || '{}');
    } catch (e) {
      claims = {};
    }

    // The theme's own finish thumbnails are not the ones in the design; swap in
    // the corner shots exported from Paper.
    var images = {};
    try {
      images = JSON.parse(fieldset.getAttribute('data-kvp-finish-images') || '{}');
    } catch (e) {
      images = {};
    }
    Array.prototype.forEach.call(fieldset.querySelectorAll('input[type="radio"]'), function (input) {
      var label = fieldset.querySelector('label[for="' + input.id + '"]');
      if (!label) return;
      label.dataset.kvpValue = input.value;
      var img = label.querySelector('img');
      if (img && images[input.value] && img.dataset.kvpFinish !== '1') {
        img.src = images[input.value];
        img.srcset = '';
        img.dataset.kvpFinish = '1';
      }
    });

    Array.prototype.forEach.call(fieldset.querySelectorAll('label .label-text'), function (span) {
      if (span.dataset.kvpDone) return;
      var raw = span.textContent.trim();
      var title = raw;
      var sub = '';
      var isClaim = false;

      var m = raw.match(/^(.*?)\s*\(([^)]*)\)\s*$/);
      if (m) {
        title = m[1].trim();
        sub = m[2].trim();
      } else if (claims[raw]) {
        sub = claims[raw];
        isClaim = true;
      }

      span.textContent = '';
      var t = document.createElement('span');
      t.className = 'kvp-title';
      t.textContent = title;
      span.appendChild(t);

      if (sub) {
        var s = document.createElement('span');
        s.className = 'kvp-sub' + (isClaim ? ' kvp-sub--claim' : '');
        s.textContent = sub;
        span.appendChild(s);
      }
      span.dataset.kvpDone = '1';
    });
  }

  /* The finish currently selected in the Product fieldset, e.g. "Framed Print". */
  function currentFinish(root) {
    var checked = root.querySelector('fieldset.optionProduct input[type="radio"]:checked');
    return checked ? checked.value : '';
  }

  /* Frame style: "Wood frame" reads as "Wood" on the card, and the swatch is the
     same corner photograph the Finish card uses. Framed Print and Framed Canvas
     are different products with different mouldings, so the swatch is keyed by
     "<finish>|<style>" — within a finish only the colour changes. */
  function enhanceStyle(fieldset) {
    var swatches = {};
    try {
      swatches = JSON.parse(fieldset.getAttribute('data-kvp-swatches') || '{}');
    } catch (e) {
      swatches = {};
    }

    var finish = currentFinish(fieldset.closest('.mixed-variant-picker') || document);

    Array.prototype.forEach.call(fieldset.querySelectorAll('input[type="radio"]'), function (input) {
      var label = fieldset.querySelector('label[for="' + input.id + '"]');
      if (!label) return;
      var img = label.querySelector('img');
      if (!img) return;
      var key = finish + '|' + input.value;
      var src = swatches[key] || swatches[input.value];
      /* Re-swap when the finish changes, not just on first run. */
      if (src && img.dataset.kvpSwatch !== key) {
        img.src = src;
        img.srcset = '';
        img.dataset.kvpSwatch = key;
      }
    });

    Array.prototype.forEach.call(fieldset.querySelectorAll('label .label-text'), function (span) {
      if (span.dataset.kvpDone) return;
      span.textContent = span.textContent.trim().replace(/\s*frames?$/i, '');
      span.dataset.kvpDone = '1';
    });
  }

  /* Hide style options that are not valid for the current finish. */
  function markUnavailable(fieldset) {
    Array.prototype.forEach.call(fieldset.querySelectorAll('input[type="radio"]'), function (input) {
      var label = fieldset.querySelector('label[for="' + input.id + '"]');
      if (!label) return;
      var off = input.disabled || input.classList.contains('disabled');
      label.classList.toggle('kvp-unavailable', off);
      if (input.value === '-') label.classList.add('kvp-unavailable');
    });
  }

  /* The design labels this "Frame Style · Wood", not "Style Wood frame". */
  function formatStyleLegend(fieldset) {
    var legend = fieldset.querySelector('legend');
    if (!legend) return;
    var checked = fieldset.querySelector('input[type="radio"]:checked');
    var value = checked ? checked.value.replace(/\s*frames?$/i, '') : '';
    if (value === '-') value = '';
    legend.textContent = '';
    var name = document.createElement('span');
    name.className = 'selectsize';
    name.textContent = 'Frame Style';
    legend.appendChild(name);
    if (value) {
      var val = document.createElement('span');
      val.className = 'sel_siz';
      val.textContent = value;
      legend.appendChild(val);
    }
  }

  /* A finish like Print has no frame styles at all; do not leave a stray
     "Style" heading with nothing under it. */
  function hideIfEmpty(fieldset) {
    var visible = fieldset.querySelectorAll('label:not(.kvp-unavailable)').length;
    // A class, not an inline style: the layout rules use !important, which an
    // inline display would lose to.
    fieldset.classList.toggle('kvp-hidden', !visible);
  }

  function wrapTrack(fieldset) {
    if (fieldset.querySelector('.kvp-track')) return;
    var track = document.createElement('div');
    track.className = 'kvp-track';
    var nodes = Array.prototype.filter.call(fieldset.childNodes, function (n) {
      return !(n.nodeType === 1 && n.tagName === 'LEGEND');
    });
    nodes.forEach(function (n) {
      track.appendChild(n);
    });
    fieldset.appendChild(track);
  }

  function init(root) {
    var picker = (root || document).querySelector('.mixed-variant-picker');
    if (!picker) return;

    var finish = picker.querySelector('.optionProduct');
    if (finish) {
      wrapTrack(finish);
      enhanceFinish(finish);
      markUnavailable(finish);
    }

    var style = picker.querySelector('.optionStyle');
    if (style) {
      wrapTrack(style);
      enhanceStyle(style);
      markUnavailable(style);
      formatStyleLegend(style);
      hideIfEmpty(style);
    }

    var sizeWrap = picker.querySelector('.size-dropdown-wrapper');
    if (sizeWrap) {
      buildSizeGrid(sizeWrap);
      syncSizeGrid(sizeWrap);
    }
  }

  function refresh() {
    var picker = document.querySelector('.mixed-variant-picker');
    if (!picker) return;
    var finish = picker.querySelector('.optionProduct');
    var style = picker.querySelector('.optionStyle');
    if (finish) markUnavailable(finish);
    if (style) {
      /* Framed Print and Framed Canvas use different mouldings, so the swatch
         has to follow the finish, not just be set once on load. */
      enhanceStyle(style);
      markUnavailable(style);
      formatStyleLegend(style);
      hideIfEmpty(style);
    }
    var sizeWrap = picker.querySelector('.size-dropdown-wrapper');
    if (sizeWrap) syncSizeGrid(sizeWrap);
  }

  /* The theme replaces the picker markup after each variant change, so we have
     to re-apply. The observer is disconnected while we mutate, otherwise our
     own DOM writes retrigger it and the page locks up. */
  var observer = null;
  var scheduled = false;
  var busy = false;

  function observe() {
    var host = document.querySelector('.mixed-variant-picker');
    if (!host) return;
    if (!observer) {
      observer = new MutationObserver(schedule);
    }
    /* Changing finish only toggles `disabled`/`class` on the style radios — the
       theme does not replace that markup — so childList alone never fires. */
    observer.observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'class']
    });
  }

  function apply() {
    scheduled = false;
    if (busy) return;
    busy = true;
    if (observer) observer.disconnect();
    try {
      init();
      refresh();
    } catch (e) {
      /* never break the buy panel */
    }
    busy = false;
    observe();

    var host = document.querySelector('.mixed-variant-picker');
    if (host) host.classList.add('kvp-ready');
  }

  function schedule() {
    if (scheduled || busy) return;
    scheduled = true;
    /* A short delay, not rAF: the theme updates the radios' disabled state over
       a couple of ticks and we need to read the settled result. */
    window.setTimeout(apply, 60);
  }

  function start() {
    /* Backstop: reveal the picker even if anything below throws. */
    setTimeout(function () {
      var h = document.querySelector('.mixed-variant-picker');
      if (h) h.classList.add('kvp-ready');
    }, 1500);
    apply();
    /* Belt and braces: a variant change always fires `change` somewhere in the
       picker, even when no attribute we watch happens to move. */
    document.addEventListener('change', function (e) {
      if (e.target && e.target.closest && e.target.closest('.mixed-variant-picker')) schedule();
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
